/**
 * Club Shop - E-commerce for Club Owners (Hardware, Subscriptions)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart, Package, Camera, Wifi, CreditCard, MapPin,
  Phone, Mail, User, Loader2, Check, Gift, Zap, Radio,
  AlertCircle, Truck, ChevronRight, Crown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import ClubNavbar from '../../components/ClubNavbar';
import { useAuthStore } from '../../store';
import api from '../../services/api';
import { toast } from 'sonner';

// Product icon mapper - returns the icon component
const productIcons = {
  'shelly_club': Radio,
  'camera_kit': Camera,
  'wifi_upgrade': Wifi,
  'pos_system': CreditCard,
  'default': Package
};

// Product card component
const ProductCard = ({ product, onSelect, selected }) => {
  const IconComponent = productIcons[product.id] || productIcons['default'];
  const isSubscription = product.product_type === 'subscription_recurring';
  const hasShipping = product.shipping_cost > 0;
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(product)}
      className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
        selected 
          ? 'border-primary bg-primary/5' 
          : 'border-border hover:border-primary/50 bg-card'
      } ${product.is_free ? 'ring-2 ring-green-500/50' : ''}`}
    >
      {/* Free badge */}
      {product.is_free && (
        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
          <Gift className="w-3 h-3" />
          GRATIS
        </div>
      )}
      
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
          selected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
        }`}>
          <IconComponent className="w-7 h-7" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base">{product.title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{product.description}</p>
          
          {/* Promo text */}
          {product.promo_text && (
            <p className="text-xs text-yellow-500 mt-2 flex items-center gap-1">
              <Gift className="w-3 h-3" />
              {product.promo_text}
            </p>
          )}
          
          {/* Free reason */}
          {product.is_free && product.free_reason && (
            <p className="text-xs text-green-500 mt-2 flex items-center gap-1">
              <Check className="w-3 h-3" />
              {product.free_reason}
            </p>
          )}
        </div>
      </div>
      
      {/* Price */}
      <div className="mt-4 pt-3 border-t border-border">
        <div className="flex items-end justify-between">
          <div>
            {product.is_free ? (
              <>
                <p className="text-2xl font-bold text-green-500">GRATIS</p>
                <p className="text-xs text-muted-foreground line-through">${product.original_price?.toLocaleString()} MXN</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold">${product.price?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">
                  MXN {isSubscription ? '/ mes' : ''}
                </p>
              </>
            )}
          </div>
          
          {/* Shipping info */}
          <div className="text-right">
            {hasShipping && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Truck className="w-3 h-3" />
                +${product.shipping_cost} envío
              </p>
            )}
            {product.shipping_note && (
              <p className="text-xs text-green-500">{product.shipping_note}</p>
            )}
            {isSubscription && product.billing_note && (
              <p className="text-xs text-muted-foreground">{product.billing_note}</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function ClubShop() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  
  // State
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [isSandbox, setIsSandbox] = useState(false);
  
  // Selection
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  
  // Checkout form
  const [formData, setFormData] = useState({
    payer_name: user?.first_name || '',
    payer_surname: user?.last_name || '',
    payer_email: user?.email || '',
    payer_phone: user?.phone || '',
    street_name: '',
    street_number: '',
    apartment: '',
    zip_code: '',
    city: '',
    state: '',
  });
  
  // Load products
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    const loadProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/shop/products/club');
        setProducts(response.data.products || []);
        setIsSandbox(response.data.is_sandbox || false);
      } catch (err) {
        console.error('Error loading products:', err);
        toast.error('Error al cargar productos');
      } finally {
        setLoading(false);
      }
    };
    
    loadProducts();
  }, [isAuthenticated, navigate]);
  
  // Update form when user loads
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        payer_name: user.first_name || prev.payer_name,
        payer_surname: user.last_name || prev.payer_surname,
        payer_email: user.email || prev.payer_email,
        payer_phone: user.phone || prev.payer_phone,
      }));
    }
  }, [user]);
  
  // Calculate totals
  const calculateTotals = () => {
    if (!selectedProduct) return { subtotal: 0, shipping: 0, total: 0 };
    
    const subtotal = selectedProduct.price * quantity;
    let shipping = selectedProduct.shipping_cost || 0;
    
    // Free shipping threshold
    if (selectedProduct.shipping_free_threshold && quantity >= selectedProduct.shipping_free_threshold) {
      shipping = 0;
    }
    
    // Free product
    if (selectedProduct.is_free) {
      return { subtotal: 0, shipping: 0, total: 0 };
    }
    
    return {
      subtotal,
      shipping,
      total: subtotal + shipping,
    };
  };
  
  const totals = calculateTotals();
  
  // Handle product selection
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setShowCheckout(false);
  };
  
  // Handle checkout
  const handleCheckout = async () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto');
      return;
    }
    
    // Validate form
    if (!formData.payer_name || !formData.payer_surname || !formData.payer_email || !formData.payer_phone) {
      toast.error('Completa todos los datos de contacto');
      return;
    }
    
    // Physical products need shipping address
    if (selectedProduct.product_type === 'physical') {
      if (!formData.street_name || !formData.street_number || !formData.zip_code || !formData.city || !formData.state) {
        toast.error('Completa la dirección de envío');
        return;
      }
    }
    
    try {
      setCheckoutLoading(true);
      
      const checkoutData = {
        product_type: selectedProduct.id,
        quantity,
        payer_name: formData.payer_name,
        payer_surname: formData.payer_surname,
        payer_email: formData.payer_email,
        payer_phone: formData.payer_phone,
        shipping_address: {
          street_name: formData.street_name,
          street_number: formData.street_number,
          apartment: formData.apartment,
          zip_code: formData.zip_code,
          city: formData.city,
          state: formData.state,
        }
      };
      
      const response = await api.post('/shop/checkout', checkoutData);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Error al crear el checkout');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <ClubNavbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24">
      <ClubNavbar />
      
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-1">
            <ShoppingCart className="w-8 h-8 text-primary" />
            <h1 className="font-display font-bold text-2xl md:text-3xl">Tienda para Clubes</h1>
          </div>
          <p className="text-muted-foreground ml-11">
            Hardware, equipos y servicios para tu club
          </p>
          
          {isSandbox && (
            <div className="mt-3 ml-11 inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-sm">
              <AlertCircle className="w-4 h-4" />
              Modo Sandbox - Pagos de prueba
            </div>
          )}
        </motion.div>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Products List */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-display font-bold text-lg">Productos Disponibles</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  selected={selectedProduct?.id === product.id}
                  onSelect={handleSelectProduct}
                />
              ))}
            </div>
          </div>
          
          {/* Checkout Panel */}
          <div className="lg:col-span-1">
            <Card className="bg-card sticky top-20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Resumen de Compra
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {selectedProduct ? (
                  <>
                    {/* Selected product */}
                    <div className="bg-secondary/50 rounded-lg p-3">
                      <p className="font-medium">{selectedProduct.title}</p>
                      <p className="text-sm text-muted-foreground">
                        ${selectedProduct.price?.toLocaleString()} MXN
                        {selectedProduct.product_type === 'subscription_recurring' && ' / mes'}
                      </p>
                    </div>
                    
                    {/* Quantity (only for physical products) */}
                    {selectedProduct.product_type === 'physical' && (
                      <div>
                        <Label>Cantidad</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            disabled={quantity <= 1}
                          >
                            -
                          </Button>
                          <span className="w-12 text-center font-bold">{quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setQuantity(Math.min(10, quantity + 1))}
                            disabled={quantity >= 10}
                          >
                            +
                          </Button>
                        </div>
                        
                        {/* Free shipping note */}
                        {selectedProduct.shipping_free_threshold && quantity < selectedProduct.shipping_free_threshold && (
                          <p className="text-xs text-muted-foreground mt-2">
                            💡 Compra {selectedProduct.shipping_free_threshold}+ para envío gratis
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Totals */}
                    <div className="space-y-2 pt-3 border-t border-border">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal</span>
                        <span>${totals.subtotal.toLocaleString()} MXN</span>
                      </div>
                      {totals.shipping > 0 && (
                        <div className="flex justify-between text-sm">
                          <span>Envío</span>
                          <span>${totals.shipping.toLocaleString()} MXN</span>
                        </div>
                      )}
                      {totals.shipping === 0 && selectedProduct.shipping_cost > 0 && (
                        <div className="flex justify-between text-sm text-green-500">
                          <span>Envío</span>
                          <span>¡GRATIS!</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                        <span>Total</span>
                        <span className="text-primary">${totals.total.toLocaleString()} MXN</span>
                      </div>
                    </div>
                    
                    {/* Checkout Form Toggle */}
                    {!showCheckout ? (
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={() => setShowCheckout(true)}
                        disabled={selectedProduct.is_free}
                      >
                        {selectedProduct.is_free ? (
                          <>
                            <Check className="w-5 h-5 mr-2" />
                            Ya lo tienes activo
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5 mr-2" />
                            Continuar al pago
                          </>
                        )}
                      </Button>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="space-y-4"
                      >
                        <h4 className="font-medium">Datos de contacto</h4>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Nombre</Label>
                            <Input
                              value={formData.payer_name}
                              onChange={(e) => setFormData({...formData, payer_name: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Apellido</Label>
                            <Input
                              value={formData.payer_surname}
                              onChange={(e) => setFormData({...formData, payer_surname: e.target.value})}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.payer_email}
                            onChange={(e) => setFormData({...formData, payer_email: e.target.value})}
                          />
                        </div>
                        
                        <div>
                          <Label>Teléfono</Label>
                          <Input
                            value={formData.payer_phone}
                            onChange={(e) => setFormData({...formData, payer_phone: e.target.value})}
                          />
                        </div>
                        
                        {/* Shipping Address (for physical products) */}
                        {selectedProduct.product_type === 'physical' && (
                          <>
                            <h4 className="font-medium pt-2 border-t border-border">Dirección de envío</h4>
                            
                            <div className="grid grid-cols-3 gap-3">
                              <div className="col-span-2">
                                <Label>Calle</Label>
                                <Input
                                  value={formData.street_name}
                                  onChange={(e) => setFormData({...formData, street_name: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Número</Label>
                                <Input
                                  value={formData.street_number}
                                  onChange={(e) => setFormData({...formData, street_number: e.target.value})}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label>C.P.</Label>
                                <Input
                                  value={formData.zip_code}
                                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Int/Depto (opcional)</Label>
                                <Input
                                  value={formData.apartment}
                                  onChange={(e) => setFormData({...formData, apartment: e.target.value})}
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label>Ciudad</Label>
                                <Input
                                  value={formData.city}
                                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                                />
                              </div>
                              <div>
                                <Label>Estado</Label>
                                <Input
                                  value={formData.state}
                                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                                />
                              </div>
                            </div>
                          </>
                        )}
                        
                        <Button
                          className="w-full"
                          size="lg"
                          onClick={handleCheckout}
                          disabled={checkoutLoading}
                        >
                          {checkoutLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Procesando...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5 mr-2" />
                              Pagar ${totals.total.toLocaleString()} MXN
                            </>
                          )}
                        </Button>
                        
                        <p className="text-xs text-muted-foreground text-center">
                          Serás redirigido a Mercado Pago
                        </p>
                      </motion.div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">Selecciona un producto para continuar</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
